import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { STORAGE_KEYS } from '@core/constants/storage-keys';
import { CartLineState, CartState } from '@core/models/cart.state';
import { FulfilmentMethod } from '@core/models/listing.enum';
import { ListingDetailState } from '@core/models/listing.state';
import { StoreState } from '@core/models/store.state';
import { BrowserStorageService } from '@core/services/browser-storage.service';
import { MockDataService } from '@core/services/mock-data.service';

import { seedListings } from '../listing/listing.seed';
import { mockError, mockResponse } from '../mock-response';
import { seedStores } from '../store/store.seed';
import { CartRepository } from './cart.repository';

/**
 * In-memory cart (HLD BUY-01). Guest carts additionally mirror to
 * localStorage so a refresh keeps the basket - a concern the real API owns
 * server-side, which is why persistence lives here and not in CartService.
 */
@Injectable()
export class CartMockRepository implements CartRepository {
  private readonly mockData = inject(MockDataService);
  private readonly storage = inject(BrowserStorageService);
  private readonly carts = this.mockData.collection<CartState>('carts');
  private readonly listings = this.mockData.collection<ListingDetailState>('listings', seedListings);
  private readonly stores = this.mockData.collection<StoreState>('stores', seedStores);

  constructor() {
    this.restoreGuestCart();
  }

  get(ownerKey: string): Observable<CartState> {
    return mockResponse(this.snapshot(this.cartFor(ownerKey)));
  }

  addLine(ownerKey: string, listingId: number, fulfilment: FulfilmentMethod): Observable<CartState> {
    const cart = this.cartFor(ownerKey);
    const listing = this.listings.get(listingId);
    if (!listing) return mockError('This item is no longer available.');

    const already = cart.lines.some(l => l.listingId === listingId);
    if (!already) {
      cart.lines.push(this.toLine(cart, listing, fulfilment));
      this.persist(cart);
    }
    return mockResponse(this.snapshot(cart));
  }

  updateLineFulfilment(ownerKey: string, lineId: number, fulfilment: FulfilmentMethod): Observable<CartState> {
    const cart = this.cartFor(ownerKey);
    const line = cart.lines.find(l => l.id === lineId);
    if (line) {
      line.chosenFulfilment = fulfilment;
      this.persist(cart);
    }
    return mockResponse(this.snapshot(cart));
  }

  removeLine(ownerKey: string, lineId: number): Observable<CartState> {
    const cart = this.cartFor(ownerKey);
    cart.lines = cart.lines.filter(l => l.id !== lineId);
    cart.savedForLater = cart.savedForLater.filter(l => l.id !== lineId);
    this.persist(cart);
    return mockResponse(this.snapshot(cart));
  }

  saveForLater(ownerKey: string, lineId: number): Observable<CartState> {
    const cart = this.cartFor(ownerKey);
    const line = cart.lines.find(l => l.id === lineId);
    if (line) {
      cart.lines = cart.lines.filter(l => l.id !== lineId);
      cart.savedForLater.push(line);
      this.persist(cart);
    }
    return mockResponse(this.snapshot(cart));
  }

  moveToCart(ownerKey: string, lineId: number): Observable<CartState> {
    const cart = this.cartFor(ownerKey);
    const line = cart.savedForLater.find(l => l.id === lineId);
    if (line) {
      cart.savedForLater = cart.savedForLater.filter(l => l.id !== lineId);
      cart.lines.push(line);
      this.persist(cart);
    }
    return mockResponse(this.snapshot(cart));
  }

  merge(guestKey: string, shopperKey: string): Observable<CartState> {
    const guest = this.cartFor(guestKey);
    const target = this.cartFor(shopperKey);

    for (const line of [...guest.lines]) {
      if (!target.lines.some(l => l.listingId === line.listingId)) {
        target.lines.push({ ...line, id: this.nextLineId(target) });
      }
    }
    for (const line of [...guest.savedForLater]) {
      if (!target.savedForLater.some(l => l.listingId === line.listingId)) {
        target.savedForLater.push({ ...line, id: this.nextLineId(target) });
      }
    }

    guest.lines = [];
    guest.savedForLater = [];
    this.persist(guest);
    this.persist(target);
    return mockResponse(this.snapshot(target));
  }

  clear(ownerKey: string): Observable<CartState> {
    const cart = this.cartFor(ownerKey);
    cart.lines = [];
    cart.savedForLater = [];
    this.persist(cart);
    return mockResponse(this.snapshot(cart));
  }

  private cartFor(ownerKey: string): CartState {
    const existing = this.carts.list().find(c => c.ownerKey === ownerKey);
    if (existing) return existing;
    return this.carts.upsert({ id: this.carts.allocateId(), ownerKey, lines: [], savedForLater: [] });
  }

  private toLine(cart: CartState, listing: ListingDetailState, fulfilment: FulfilmentMethod): CartLineState {
    const store = this.stores.get(listing.storeId);
    const chosen = listing.pickupOnly ? FulfilmentMethod.Pickup : fulfilment;
    return {
      id: this.nextLineId(cart),
      listingId: listing.id,
      title: listing.title,
      condition: listing.condition,
      conditionNote: listing.conditionNote,
      format: listing.format,
      unitPrice: listing.buyNowPrice ?? listing.price ?? 0,
      photoUrl: listing.primaryPhotoUrl,
      tenantId: listing.tenantId,
      tenantName: listing.tenantName,
      storeId: listing.storeId,
      storeName: listing.storeName,
      storeAddressLine: store?.addressLine ?? '',
      storeHoursLabel: store?.hoursLabel ?? '',
      distanceMi: listing.distanceMi,
      pickupAvailable: listing.pickupAvailable,
      pickupOnly: listing.pickupOnly,
      shippingPrice: listing.shippingPrice,
      chosenFulfilment: chosen,
      addedAt: new Date()
    };
  }

  private nextLineId(cart: CartState): number {
    const max = Math.max(0, ...cart.lines.map(l => l.id), ...cart.savedForLater.map(l => l.id));
    return max + 1;
  }

  /** Deep-copy so store mutations never leak references to feature code. */
  private snapshot(cart: CartState): CartState {
    return {
      ...cart,
      lines: cart.lines.map(l => ({ ...l })),
      savedForLater: cart.savedForLater.map(l => ({ ...l }))
    };
  }

  private persist(cart: CartState): void {
    this.carts.upsert(cart);
    if (cart.ownerKey.startsWith('guest-')) {
      this.storage.setLocalJson(STORAGE_KEYS.GUEST_CART, cart);
    }
  }

  private restoreGuestCart(): void {
    const saved = this.storage.getLocalJson<CartState>(STORAGE_KEYS.GUEST_CART);
    if (!saved || !saved.ownerKey?.startsWith('guest-')) return;
    const revive = (line: CartLineState): CartLineState => ({ ...line, addedAt: new Date(line.addedAt) });
    this.carts.upsert({
      ...saved,
      id: this.carts.allocateId(),
      lines: (saved.lines ?? []).map(revive),
      savedForLater: (saved.savedForLater ?? []).map(revive)
    });
  }
}
